import { Footer } from 'flowbite-react'
import React, { memo, useEffect, useState } from 'react'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import { Text } from 'react-font'

import ServiceCard from '@/components/Card'
import Navbar from '@/components/NavBar'
import Content from '@/components/Page/Content'
import { WHATSAPP_NUMBER } from '@/constants'
interface Props {}

const NAV_OFFSET = 110

const FloatingScheduleButton: React.FC = memo(() => {
  const [isSchedulingVisible, setIsSchedulingVisible] = useState(false)

  useEffect(() => {
    const target = document.querySelector('#scheduling')
    if (!target) return
    // Hide the button once the scheduling section itself crosses the
    // vertical center of the viewport, so it's not offering to jump
    // somewhere the user is already looking at.
    const observer = new IntersectionObserver(
      ([entry]) => setIsSchedulingVisible(entry.isIntersecting),
      { rootMargin: '-50% 0px -50% 0px' },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  if (isSchedulingVisible) return null

  return (
    <AnchorLink
      href="#scheduling"
      offset={NAV_OFFSET}
      aria-label="Ir para agendamento de consulta"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-brand-accent px-5 py-3 font-bold text-brand-dark shadow-lg hover:bg-brand-dark hover:text-brand-cream transition-colors"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-5 h-5"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <span>Agendar Consulta</span>
    </AnchorLink>
  )
})
FloatingScheduleButton.displayName = 'FloatingScheduleButton'

const AgendarButton: React.FC = memo(() => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}`}
    target="_blank"
    rel="noopener noreferrer"
    className="bg-brand-sage hover:bg-brand-taupe text-brand-dark hover:text-brand-cream font-bold py-2 px-4 rounded-full inline-flex items-center transition-colors"
  >
    <svg viewBox="0 0 32 32" className="fill-current w-5 h-5 mr-2">
      <path
        d=" M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.244-.73.244-1.088 0-.058 0-.144-.03-.215-.1-.172-2.434-1.39-2.678-1.39zm-2.908 7.593c-1.747 0-3.48-.53-4.942-1.49L7.793 24.41l1.132-3.337a8.955 8.955 0 0 1-1.72-5.272c0-4.955 4.04-8.995 8.997-8.995S25.2 10.845 25.2 15.8c0 4.958-4.04 8.998-8.998 8.998zm0-19.798c-5.96 0-10.8 4.842-10.8 10.8 0 1.964.53 3.898 1.546 5.574L5 27.176l5.974-1.92a10.807 10.807 0 0 0 16.03-9.455c0-5.958-4.842-10.8-10.802-10.8z"
        fillRule="evenodd"
      ></path>
    </svg>
    <span>Agendar</span>
  </a>
))
AgendarButton.displayName = 'AgendarButton'

const Index: React.FC<Props> = memo(() => {
  return (
    <>
      <Navbar />
      <h1 className="sr-only">Mariana Castelo | Nutrição Materno Infantil</h1>
      <div id="top" className="mx-4 md:mx-20 -mt-4 -mb-6">
        <picture>
          <source
            type="image/webp"
            srcSet="/banner-800w.webp 800w, /banner-1600w.webp 1600w"
            sizes="(min-width: 768px) 1120px, 100vw"
          />
          <img
            className="w-full h-auto"
            src="/banner-1600w.jpg"
            alt="Foto de Mariana"
            width={1600}
            height={533}
            decoding="async"
          />
        </picture>
      </div>
      <Content id="about" title="Sobre mim">
        <div className="space-y-4">
          <p className="text-justify">
            Atuo na área materno infantil desde 2015. Atendo gestantes,
            lactantes, bebês, crianças e adolescentes.Busco orientar os cuidados
            em saúde através de uma nutrição consciente, acessível, leve e
            gentil. Sem terrorismos e modismos, recheada de sugestões simples,
            de boa procedência, com bons ingredientes e nutrientes e cheinha de
            aroma. Prezo pela boa relação com os alimentos desde cedo, para
            tanto oriento e estimulo a conexão com as refeições, o envolvimento
            no preparo e a autonomia no dia a dia.
          </p>
        </div>
      </Content>

      <Content id="services" title="Serviços">
        <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
          <ServiceCard title="Gestação" imgSrc="acomp_materno.jpeg">
            Cada fase da gravidez pede um cuidado diferente. Um acompanhamento
            nutricional pensado para você e seu bebê, trimestre a trimestre,
            respeitando seus hábitos, sua rotina e qualquer condição de saúde
            que precise de atenção especial. O objetivo é simples: prevenir
            complicações, aliviar aqueles desconfortos tão comuns da gestação e
            ajudar você a viver essa fase com mais leveza, segurança e saúde
            para você e para quem está a caminho.
          </ServiceCard>

          <ServiceCard title="Lactação" imgSrc="acomp_lac.jpeg">
            Nessa fase, o cuidado é com as duas vidas ao mesmo tempo: a sua e a
            do seu bebê. Por isso, o foco é garantir que você receba todos os
            nutrientes de que precisa para se recuperar e ter energia, enquanto
            seu bebê recebe, através do leite materno, tudo o que é essencial
            para crescer forte e saudável.
          </ServiceCard>

          <ServiceCard title="Introdução Alimentar" imgSrc="acomp_intro.jpeg">
            Chegou a hora de apresentar ao seu bebê um mundo novo de sabores e
            texturas. Nessa fase tão especial, você recebe todas as orientações
            e práticas necessárias para tornar essa transição para os alimentos
            sólidos leve, segura e cheia de descobertas.
          </ServiceCard>

          <ServiceCard title="Criança" imgSrc="acomp_crianca.jpg">
            Cada criança tem seu próprio ritmo, seus gostos e seus desafios à
            mesa — e é justamente isso que considero. Acompanho de perto a
            alimentação e o desenvolvimento nutricional do seu filho,
            respeitando sua rotina, seus hábitos, eventuais dificuldades
            alimentares e qualquer condição de saúde que precise de atenção,
            para garantir um crescimento saudável e cheio de energia.
          </ServiceCard>

          <ServiceCard title="Adolescente" imgSrc="acomp_adolescente.jpeg">
            A adolescência traz mudanças rápidas no corpo, nos hábitos e até na
            relação com a comida. Por isso, acompanhamos de perto a alimentação
            e o desenvolvimento nutricional do adolescente, respeitando sua
            rotina, seus hábitos, eventuais dificuldades alimentares e qualquer
            condição de saúde que precise de atenção, garantindo o suporte
            nutricional necessário para atravessar essa fase com saúde e
            equilíbrio.
          </ServiceCard>
        </div>
      </Content>

      <Content id="scheduling" title="Agendamento de Consulta">
        <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
          <ServiceCard title="Atendimento Online" footer={<AgendarButton />}>
            Consultas por videochamada, com todo o suporte e acompanhamento de
            perto, para quem prefere atendimento a distância.
          </ServiceCard>

          <ServiceCard
            title="Clínica Ponte Health Center"
            footer={<AgendarButton />}
          >
            Rua Antônio Augusto, 1271, Fortaleza - CE.
          </ServiceCard>

          <ServiceCard
            title="Clínica Pequenino Mundo"
            footer={<AgendarButton />}
          >
            Avenida Eusébio de Queiroz, 4808, Eusébio - CE.
          </ServiceCard>
        </div>
      </Content>

      <Footer container className="bg-brand-cream">
        <div className="w-full text-center">
          <div className="flex flex-col md:flex-row items-center md:items-stretch gap-6 md:gap-4 md:h-20">
            <div id="footer-left" className="hidden md:block md:w-1/3"></div>
            <div
              id="footer-center"
              className="w-full md:w-1/3 flex flex-row justify-center gap-4"
            >
              <a href="https://www.crn11.org.br/" target="_blank">
                <img
                  src="crn11.png"
                  className="max-h-20 w-auto shrink-0"
                  alt="Conselho Regional de Nutricionistas 11ª Região"
                />
              </a>
              <a href="https://www.cfn.org.br/" target="_blank">
                <img
                  src="cfn.png"
                  className="max-h-20 w-auto shrink-0"
                  alt="Conselho Federal de Nutricionistas"
                />
              </a>
            </div>
            <div
              id="footer-right"
              className="w-full md:w-1/3 flex justify-center md:justify-end"
            >
              <a
                href="https://www.instagram.com/marianacastelonutri/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-brand-taupe hover:text-brand-dark transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current h-8 shrink-0"
                  viewBox="0 0 448 512"
                >
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                </svg>
                <Text family="Roboto">@maricastelonutri</Text>
              </a>
            </div>
          </div>
          <Footer.Divider />
          <Footer.Copyright
            by="Mariana Castelo Pinheiro"
            year={new Date().getFullYear()}
          />
        </div>
      </Footer>
      <FloatingScheduleButton />
    </>
  )
})
Index.displayName = 'Index'

export default Index
